import axios, { AxiosInstance, AxiosError } from 'axios'
import { AUTH_TOKEN_KEY } from '@/config/constants'
import { clearAuth, getRefreshToken, setAuthToken, setRefreshToken } from '@/utils/auth'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'

/** Default axios timeout for most API calls (ms). */
export const DEFAULT_API_TIMEOUT_MS = 30_000
/**
 * Xero-backed and dashboard aggregate endpoints can exceed 30s in production
 * (backend allows up to 90s per Xero HTTP call).
 */
export const SLOW_API_TIMEOUT_MS = 120_000

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** When true, failed requests do not trigger the global ApiErrorToast. */
    suppressGlobalError?: boolean
  }
}

export type ApiErrorListener = (message: string) => void
const errorListeners = new Set<ApiErrorListener>()

export function onApiError(listener: ApiErrorListener) {
  errorListeners.add(listener)
  return () => { errorListeners.delete(listener) }
}

function notifyError(message: string) {
  errorListeners.forEach((fn) => fn(message))
}

function isIntegrationAuthFailure(error: AxiosError): boolean {
  const data = (error.response?.data ?? {}) as {
    message?: string
    error?: { message?: string }
  }
  const msg = (data.error?.message || data.message || '').toLowerCase()
  return msg.includes('xero authentication failed') || msg.includes('authorizationunsuccessful')
}

export function friendlyErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const err = error as AxiosError<{
      message?: string
      error?: {
        message?: string
        details?: {
          action?: string
          requiredRoles?: string[]
          currentRole?: string | null
        }
      }
    }>

    if (err.response?.data) {
      const msg = err.response.data.error?.message ?? err.response.data.message
      if (typeof msg === 'string' && msg) return msg
    }

    if (err.response) {
      const status = err.response.status
      if (status === 401) return 'Session expired. Please sign in again.'
      if (status === 403) {
        const details = err.response.data?.error?.details
        const action = details?.action ? ` (${details.action})` : ''
        const requiredRoles = Array.isArray(details?.requiredRoles) && details?.requiredRoles.length > 0
          ? ` Required roles: ${details.requiredRoles.join(', ')}.`
          : ''
        return `You do not have permission to perform this action${action}.${requiredRoles}`.trim()
      }
      if (status === 404) return 'The requested resource was not found.'
      if (status === 409) return 'A conflict occurred. The data may have been modified by someone else.'
      if (status === 422) return 'Invalid data submitted. Please check your input.'
      if (status >= 500) return 'The server encountered an error. Please try again later.'
      return `Request failed (${status}). Please try again.`
    }

    if (!err.response) {
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        return 'The request timed out. The server may still be finishing the sync — try again in a moment.'
      }
      if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
        return `Cannot reach the server at ${API_BASE_URL}. Please check that the backend is running and accessible.`
      }
      return 'A network error occurred. Please check your internet connection.'
    }
  }

  if (error instanceof Error) return error.message
  return 'An unexpected error occurred. Please try again.'
}

class ApiService {
  private api: AxiosInstance
  private isRefreshing = false
  private refreshPromise: Promise<string | null> | null = null

  private async refreshAccessToken(): Promise<string | null> {
    const storedRefreshToken = getRefreshToken()
    if (!storedRefreshToken) return null

    type RefreshResponse = {
      success?: boolean
      data?: { token?: string; refreshToken?: string }
      token?: string
      refreshToken?: string
    }

    try {
      const response = await axios.post<RefreshResponse>(`${API_BASE_URL}/auth/refresh`, {
        refreshToken: storedRefreshToken,
      })
      const payload = response.data
      const nextToken = payload?.data?.token ?? payload?.token
      const nextRefreshToken = payload?.data?.refreshToken ?? payload?.refreshToken

      if (!nextToken) return null

      setAuthToken(nextToken)
      if (nextRefreshToken) setRefreshToken(nextRefreshToken)

      return nextToken
    } catch {
      return null
    }
  }

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: DEFAULT_API_TIMEOUT_MS,
    })

    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY)
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          const config = error.config as (any & { _retried?: boolean }) | undefined

          // Try to refresh once before forcing logout.
          if (config && !config._retried && !isIntegrationAuthFailure(error)) {
            config._retried = true

            if (!this.isRefreshing) {
              this.isRefreshing = true
              this.refreshPromise = this.refreshAccessToken().finally(() => {
                this.isRefreshing = false
                this.refreshPromise = null
              })
            }

            const nextToken = await this.refreshPromise
            if (nextToken) {
              config.headers = config.headers ?? {}
              config.headers.Authorization = `Bearer ${nextToken}`
              return this.api.request(config)
            }
          }

          // Do not force app logout for third-party integration auth failures (e.g. Xero token expiry).
          // Those should be handled in-page so users don't lose their whole session.
          if (!isIntegrationAuthFailure(error)) {
            clearAuth()
          }
        }

        // Auto-retry once on 429 with back-off
        const config = error.config as any
        if (error.response?.status === 429 && config && !config._retried) {
          config._retried = true
          const retryAfter = parseInt(error.response.headers['retry-after'] || '3', 10)
          const waitMs = Math.min(retryAfter * 1000, 10000)
          await new Promise((r) => setTimeout(r, waitMs))
          return this.api.request(config)
        }

        const msg = friendlyErrorMessage(error)
        const suppressGlobalError = (config as { suppressGlobalError?: boolean } | undefined)?.suppressGlobalError
        if (!suppressGlobalError) {
          notifyError(msg)
        }

        const enriched = new Error(msg)
        ;(enriched as any).originalError = error
        ;(enriched as any).status = error.response?.status
        return Promise.reject(enriched)
      }
    )
  }

  get instance() {
    return this.api
  }

  // Helper method to normalize URLs - removes /api prefix if present since baseURL already includes it
  private normalizeUrl(url: string): string {
    // Remove leading /api if present, since baseURL already includes /api
    if (url.startsWith('/api/')) {
      return url.substring(4) // Remove '/api' prefix
    }
    return url
  }

  // Generic methods
  async get<T>(url: string, config?: any): Promise<T> {
    const normalizedUrl = this.normalizeUrl(url)
    const response = await this.api.get<T>(normalizedUrl, config)
    return response.data
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const normalizedUrl = this.normalizeUrl(url)
    const response = await this.api.post<T>(normalizedUrl, data, config)
    return response.data
  }

  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    const normalizedUrl = this.normalizeUrl(url)
    const response = await this.api.put<T>(normalizedUrl, data, config)
    return response.data
  }

  async delete<T>(url: string, config?: any): Promise<T> {
    const normalizedUrl = this.normalizeUrl(url)
    const response = await this.api.delete<T>(normalizedUrl, config)
    return response.data
  }

  async patch<T>(url: string, data?: any, config?: any): Promise<T> {
    const normalizedUrl = this.normalizeUrl(url)
    const response = await this.api.patch<T>(normalizedUrl, data, config)
    return response.data
  }

  // File upload method
  async uploadFile<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const normalizedUrl = this.normalizeUrl(url)
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.api.post<T>(normalizedUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(percentCompleted)
        }
      },
    })
    return response.data
  }
}

export const apiService = new ApiService()
export default apiService

